import { Autocomplete } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { Form, Formik, FormikErrors } from "formik";
import * as React from "react";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import ProgressIndicator from "../../components/ProgressIndicator";
import strings from "../../config/localization";
import {
  useNewPersonnelMutation,
  useGetAllDesignationsQuery,
  useUploadPhotoMutation,
} from "../../services/appln";
import ErrorMsg from "../../components/ErrorMsg";

interface FormData {
  name: string;
  designationId: string;
  idNumber: string;
  mobileNumber: string;
  district: string;
  organization: string;
}

interface Props {
  onSubmitHandler: (newObj: any) => void;
  onCancelHandler: () => void;
  state: boolean;
}

export default function NewPersonnel(props: Props) {
  const [createPersonnel, createPersonnelParams] = useNewPersonnelMutation();
  const [uploadPhoto, uploadPhotoParams] = useUploadPhotoMutation();

  const [photo, setPhoto] = React.useState<any>("");
  const [id, setID] = React.useState(undefined);

  const iv: FormData = {
    name: "",
    designationId: "",
    idNumber: "",
    mobileNumber: "",
    district: "",
    organization: "",
  };

  const designationData = useGetAllDesignationsQuery({
    page: 1,
    limt: 100,
    search: "",
  });

  const designationMap = new Map<string, string>();
  designationData.data?.data.forEach((element: any) => {
    designationMap.set(element.name, element.id);
  });

  const onCancelHandlerWrapper = () => {
    createPersonnelParams.reset();
    props.onCancelHandler();
  };

  console.log("NewPersonnel, createPersonnelParams:", createPersonnelParams);
  React.useEffect(() => {
    if (createPersonnelParams.isSuccess) {
      if (photo === "") {
        props.onSubmitHandler(createPersonnelParams.data.id);
      } else {
        setID(createPersonnelParams.data.id);
        const formData = new FormData();
        formData.append("photo", photo);
        uploadPhoto({
          personnelId: createPersonnelParams.data.id,
          formData: formData,
        });
      }
    }
  }, [createPersonnelParams]);

  React.useEffect(() => {
    if (uploadPhotoParams.isSuccess) {
      props.onSubmitHandler(id);
    }
  }, [uploadPhotoParams]);

  return (
    <div>
      <Dialog
        open={props.state}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">
          {strings.newPersonnel}
        </DialogTitle>
        {createPersonnelParams.isError && (
          <ErrorMsg msg={strings.failedToCreatePersonnel} />
        )}
        {(createPersonnelParams.isLoading || uploadPhotoParams.isLoading) && (
          <ProgressIndicator />
        )}
        <DialogContent>
          <Formik
            initialValues={iv}
            validate={(values) => {
              const errors: FormikErrors<FormData> = {};
              if (!values.name) {
                errors.name = strings.nameMandatory;
              }
              return errors;
            }}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(false);
              const valuesClone = { ...values };
              valuesClone.designationId = designationMap.get(
                values.designationId!,
              )!;
              createPersonnel(valuesClone);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
            }) => (
              <Form>
                <Box sx={{ flexGrow: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        margin="normal"
                        id="name"
                        name="name"
                        label={strings.name}
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Autocomplete
                        fullWidth
                        disablePortal
                        id="designationId"
                        options={Array.from(designationMap.keys())}
                        renderInput={(params) => (
                          <TextField {...params} label={strings.designation} />
                        )}
                        onChange={(e, value: any) => {
                          setFieldValue("designationId", value);
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        margin="normal"
                        id="idNumber"
                        name="idNumber"
                        label={strings.idNumber}
                        value={values.idNumber}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        helperText={touched.idNumber}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        margin="normal"
                        id="mobileNumber"
                        name="mobileNumber"
                        label={strings.mobileNumber}
                        value={values.mobileNumber}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        helperText={touched.mobileNumber}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <input
                        id="photo"
                        name="photo"
                        type="file"
                        onChange={(event) => {
                          if (event && event.target) {
                            const target = event.target as HTMLInputElement;
                            if (target.files) {
                              const file = target.files[0];
                              setPhoto(file);
                            }
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={3}>
                      <ButtonGroup>
                        <Button
                          variant="contained"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {strings.create}
                        </Button>
                        <Button
                          variant="contained"
                          onClick={onCancelHandlerWrapper}
                        >
                          {strings.cancel}
                        </Button>
                      </ButtonGroup>
                    </Grid>
                  </Grid>
                </Box>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </div>
  );
}
